import React from 'react';
import { Link } from 'react-router-dom';
import { services } from '../mockData';
import { Users, Settings, Shield, Eye, CheckCircle, GitBranch, Award, Package } from 'lucide-react';
import { Button } from '../components/ui/button';

const iconMap = {
  Users: Users,
  Settings: Settings,
  Shield: Shield,
  Eye: Eye,
  Package: Package
};

const Services = () => {
  return (
    <div style={{ backgroundColor: '#1a1c1b', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{
        padding: '120px 40px 80px',
        backgroundColor: '#302f2c',
        borderBottom: '1px solid rgba(63, 72, 22, 0.5)'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            lineHeight: 0.95,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            Our Services
          </h1>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '50rem',
            margin: '0 auto'
          }}>
            Comprehensive workforce solutions from a single, trusted partner
          </p>
        </div>
      </section>

      {/* Core Offering */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          textAlign: 'center',
          marginBottom: '64px'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '24px'
          }}>
            Workforce Aggregation
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            maxWidth: '60rem',
            margin: '0 auto 24px'
          }}>
            We seamlessly coordinate labour from multiple suppliers to ensure continuity, compliance, and capability across all project stages.
          </p>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.1rem',
            lineHeight: 1.5,
            color: '#888680',
            maxWidth: '60rem',
            margin: '0 auto',
            fontWeight: 500,
            fontStyle: 'italic'
          }}>
            Workforce and supply coordination, simplified.
          </p>
        </div>

        {/* Services Grid */}
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '32px'
        }}>
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div
                key={index}
                style={{
                  backgroundColor: '#302f2c',
                  padding: '40px',
                  borderRadius: '8px',
                  border: '1px solid rgba(63, 72, 22, 0.5)',
                  transition: 'transform 0.3s ease, background-color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.backgroundColor = '#3f4816';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backgroundColor = '#302f2c';
                }}
              >
                <IconComponent size={48} style={{ color: '#d9fb06', marginBottom: '20px' }} />
                <h3 style={{
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                  color: '#d9fb06',
                  marginBottom: '16px',
                  textTransform: 'uppercase'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  fontFamily: 'Inter, Arial, sans-serif',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  color: '#dfddd6'
                }}>
                  {service.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Service Details */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#302f2c'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            textTransform: 'uppercase',
            marginBottom: '48px',
            textAlign: 'center'
          }}>
            What We Deliver
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {[
              { icon: Users, text: 'Skilled labour across all major industries' },
              { icon: GitBranch, text: 'Individual workers or complete teams' },
              { icon: Award, text: 'Supervisors and specialist personnel' },
              { icon: CheckCircle, text: 'Project-based workforce delivery' },
              { icon: Shield, text: 'Strict safety and insurance compliance' },
              { icon: Settings, text: 'Centralised contract management' }
            ].map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: '#1a1c1b',
                    padding: '24px',
                    borderRadius: '8px',
                    border: '1px solid rgba(63, 72, 22, 0.5)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    transition: 'background-color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3f4816'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1a1c1b'}
                >
                  <IconComponent size={24} style={{ color: '#d9fb06', flexShrink: 0, marginTop: '4px' }} />
                  <p style={{
                    fontFamily: 'Inter, Arial, sans-serif',
                    fontSize: '1rem',
                    color: '#dfddd6',
                    margin: 0,
                    lineHeight: 1.5
                  }}>
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Training & Supervision */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#1a1c1b'
      }}>
        <div style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '40px'
        }}>
          <div style={{
            backgroundColor: '#302f2c',
            padding: '40px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)'
          }}>
            <Award size={40} style={{ color: '#d9fb06', marginBottom: '20px' }} />
            <h3 style={{
              fontFamily: 'Arial, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#d9fb06',
              marginBottom: '16px',
              textTransform: 'uppercase'
            }}>
              Training & Onboarding Oversight
            </h3>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: '#dfddd6',
              marginBottom: '16px'
            }}>
              We ensure suppliers and clients align WHS expectations through:
            </p>
            <ul style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.8,
              color: '#888680',
              paddingLeft: '20px'
            }}>
              <li>Induction verification</li>
              <li>Training compliance checks</li>
              <li>Work readiness documentation</li>
            </ul>
          </div>

          <div style={{
            backgroundColor: '#302f2c',
            padding: '40px',
            borderRadius: '8px',
            border: '1px solid rgba(63, 72, 22, 0.5)'
          }}>
            <Eye size={40} style={{ color: '#d9fb06', marginBottom: '20px' }} />
            <h3 style={{
              fontFamily: 'Arial, sans-serif',
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#d9fb06',
              marginBottom: '16px',
              textTransform: 'uppercase'
            }}>
              Project Supervision
            </h3>
            <p style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.6,
              color: '#dfddd6',
              marginBottom: '16px'
            }}>
              Suppliers allocate qualified Nominated Supervisors who:
            </p>
            <ul style={{
              fontFamily: 'Inter, Arial, sans-serif',
              fontSize: '1rem',
              lineHeight: 1.8,
              color: '#888680',
              paddingLeft: '20px'
            }}>
              <li>Oversee onsite delivery</li>
              <li>Liaise with client representatives</li>
              <li>Maintain safety and quality standards</li>
              <li>Ensure workforce performance</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '96px 40px',
        backgroundColor: '#3f4816',
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '60rem',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontFamily: 'Arial, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            lineHeight: 1.1,
            color: '#d9fb06',
            marginBottom: '24px',
            textTransform: 'uppercase'
          }}>
            Let's Build Your Workforce
          </h2>
          <p style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: '1.25rem',
            lineHeight: 1.6,
            color: '#dfddd6',
            marginBottom: '40px'
          }}>
            Connect with our team to discuss your workforce requirements
          </p>
          <Link to="/contact?form=workforce" style={{ textDecoration: 'none' }}>
            <Button
              style={{
                background: '#d9fb06',
                color: '#1a1c1b',
                border: 'none',
                borderRadius: '10rem',
                padding: '1.2em 2em',
                fontFamily: 'Inter, Arial, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              Request Workforce
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
